import { useState } from "react";
import toast from "react-hot-toast";
import { Calculator, Plus } from "lucide-react";
import {
  useJournalEntries,
  usePostJournalEntry,
  JOURNAL_ENTRY_STATUS_LABEL,
  JournalEntryStatus,
  journalEntryTotal,
} from "@/api/accounting";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateJournalEntryModal from "@/pages/accounting/CreateJournalEntryModal";

const STATUS_STYLE: Record<JournalEntryStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  CONTABILIZADO: "bg-green-100 text-green-700",
  ANULADO: "bg-red-100 text-red-700",
};

export default function AccountingSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useJournalEntries({ clientId, pageSize: 50 });
  const postEntry = usePostJournalEntry();
  const [showCreate, setShowCreate] = useState(false);

  const handlePost = async (id: string) => {
    try {
      await postEntry.mutateAsync(id);
      toast.success("Asiento contabilizado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Contabilidad</h2>
        {hasPermission("accounting.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nuevo asiento
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando asientos contables...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene asientos contables registrados.</p>
        )}
        {data?.data.map((entry) => (
          <div key={entry.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Calculator size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  N.° {entry.entryNumber} · {entry.description}
                </p>
                <p className="text-xs text-gray-500">
                  {entry.entryDate} ·{" "}
                  {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(
                    journalEntryTotal(entry)
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {entry.status === "BORRADOR" && hasPermission("accounting.update") && (
                <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handlePost(entry.id)}>
                  Contabilizar
                </button>
              )}
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[entry.status]}`}>
                {JOURNAL_ENTRY_STATUS_LABEL[entry.status]}
              </span>
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateJournalEntryModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
